import { createContext, useContext, useEffect, useState } from 'react'
import { authApi } from '../api/auth'
import { ouraApi } from '../api/oura'
import { clearOnboardingDraft } from '../utils/onboardingDraft'
import { clearHealthKitConnection } from '../services/healthkit'

const AuthContext = createContext(null)

// Token persistence storage key. Switched from ``sessionStorage`` to
// ``localStorage`` on May 28 2026: sessionStorage clears on tab/browser
// close, so F&F users on iOS Safari (which aggressively recycles tabs)
// were being thrown back to /login on every cold open. localStorage
// survives until the user explicitly logs out OR the access token's
// JWT expires server-side — at which point the /auth/me call below
// returns 401 and we clean up here.
//
// Storage key kept as ``haven_token`` for compatibility — both the API
// client and the auth context read/write the same name.
const TOKEN_STORAGE_KEY = 'haven_token'

function _readToken() {
  // Wrapped in try/catch because some browsers throw on storage access
  // in private mode. Falling back to null is equivalent to "no session"
  // and the user gets the standard login path.
  try { return localStorage.getItem(TOKEN_STORAGE_KEY) } catch { return null }
}

function _writeToken(value) {
  try { localStorage.setItem(TOKEN_STORAGE_KEY, value) } catch { /* private mode */ }
}

function _clearToken() {
  try { localStorage.removeItem(TOKEN_STORAGE_KEY) } catch { /* private mode */ }
  // Apple Health's local connection flags are device-scoped, not
  // user-scoped: without this they survive sign-out and the next
  // account on the same device inherits a connection it never made.
  clearHealthKitConnection()
}


export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  // Restore-on-mount: read the persisted token before the first render
  // so authenticated routes don't briefly flicker to the login screen
  // while the AuthProvider hydrates.
  const [token, setToken] = useState(_readToken)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!token) {
      setLoading(false)
      return
    }
    authApi.me(token)
      .then(u => {
        setUser(u)
        // Heartbeat: if the user's profile claims Oura is connected, poke
        // the backend to validate / refresh the stored token. Fire-and-forget
        // so a slow Oura response doesn't block app rendering.
        if (u?.oura_connected) {
          ouraApi.heartbeat(token).catch(() => {})
        }
      })
      .catch(() => {
        // /auth/me 401 means the JWT expired or was rotated server-side.
        // Drop the dead token so the next render bounces to /login.
        _clearToken()
        setToken(null)
      })
      .finally(() => setLoading(false))
  }, [token])

  function _persist(data) {
    _writeToken(data.access_token)
    setToken(data.access_token)
    setUser(data.user)
  }

  async function login(email, password) {
    const data = await authApi.login(email, password)
    _persist(data)
    return data.user
  }

  async function signup(email, password, name) {
    const data = await authApi.signup(email, password, name)
    _persist(data)
    return data.user
  }

  async function loginWithGoogle(credential) {
    const data = await authApi.googleAuth(credential)
    _persist(data)
    return data.user
  }

  function patchUser(fields) {
    setUser(prev => prev ? { ...prev, ...fields } : prev)
  }

  function logout() {
    _clearToken()
    // Drop any in-progress onboarding draft so the next user on a shared
    // browser can't restore the previous user's DOB into their session.
    clearOnboardingDraft()
    setToken(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, loginWithGoogle, logout, patchUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
